import React, { useState, useRef, useEffect, useCallback } from "react";
import { Resizable } from "react-resizable";
import "react-resizable/css/styles.css";
import { setDragging } from "../utils/dragState";
import { throttle } from 'lodash';

export const ResizableHeaderCell = ({ onResize, width, children }) => {
  // Track if we're currently dragging
  const [isDragging, setIsDragging] = useState(false);
  // Track hover state for the resize handle
  const [isHovered, setIsHovered] = useState(false);
  const dragStartPos = useRef(null);

  // Prevent click events from propagating to the parent (sort handler)
  const handleMouseDown = (e) => {
    // Capture start position
    dragStartPos.current = { x: e.clientX, y: e.clientY };

    // Stop propagation immediately
    e.stopPropagation();
    e.preventDefault();

    // Set both local and global drag state
    setIsDragging(true);
    setDragging(true);

    // Add document-level event listener for click blocking
    document.addEventListener("click", blockClickDuringDrag, true);
  };

  // Block any click events during and immediately after drag
  const blockClickDuringDrag = (e) => {
    if (isDragging) {
      e.stopPropagation();
      e.preventDefault();
    }
  };

const throttledSetDragging = useRef(
  throttle(() => setDragging(true), 200)
).current;

const handleResize = useCallback((e, data) => {
  throttledSetDragging();
  onResize(e, data);
}, [onResize]);

  // Reset dragging state on mouse up
  useEffect(() => {
    const handleMouseUp = (e) => {
      if (isDragging) {
        // Prevent the column sort from triggering
        e.stopPropagation();
        e.preventDefault();

        // Keep dragging state active briefly to block any potential sort
        setTimeout(() => {
          // Reset both local and global drag state after a short delay
          setIsDragging(false);
          setDragging(false);

          // After resetting drag states, remove the click blocking listener
          setTimeout(() => {
            document.removeEventListener("click", blockClickDuringDrag, true);
          }, 100);
        }, 300); // Keep dragging state true for 300ms to block any sort
      }
    };

    document.addEventListener("mouseup", handleMouseUp, true);

    return () => {
      document.removeEventListener("mouseup", handleMouseUp, true);
      document.removeEventListener("click", blockClickDuringDrag, true);

      // Reset global state when component unmounts
      if (isDragging) {
        setDragging(false);
      }
    };
  }, [isDragging]);

  return (
    <div style={{ position: "relative", height: "100%" }}>
      <Resizable
        width={parseInt(width, 10) || 200}
        height={0}
        onResize={handleResize}
        draggableOpts={{ enableUserSelectHack: false }}
        resizeHandles={["e"]}
        minConstraints={[200, 0]} // Enforce minimum width of 200px
        maxConstraints={[5000, 0]} // Add a reasonable maximum width
        handle={
          <div
            className="resize-handle"
            style={{
              position: "absolute",
              right: 0,
              top: 0,
              bottom: 0,
              width: "30px",
              cursor: "col-resize",
              zIndex: 999,
            }}
            onMouseDown={handleMouseDown}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
          >
            {/* Left/right arrow indicator visible only on hover */}
            <div
              style={{
                position: "absolute",
                left: "50%",
                top: "50%",
                transform: "translate(-50%, -50%)",
                opacity: isHovered ? 1 : 0,
                transition: "opacity 0.2s ease",
                fontSize: "16px",
                pointerEvents: "none", // prevents interference with dragging
              }}
            >
              &#8596;
            </div>
          </div>
        }
      >
        <div
          style={{
            width: `${parseInt(width, 10) - 50}px`,
            display: "flex",
            alignItems: "center",
            overflow: "hidden",
            height: "50px",
          }}
        >
          {children}
        </div>
      </Resizable>
    </div>
  );
};
